import { Component, OnInit } from '@angular/core';
import { Routes, Router } from '@angular/router';
import { EChartsOption } from 'echarts';
import { HabilitationsService } from 'src/app/data/habilitation/services/habilitations/habilitations.service';
import { NavigationContext, SessionContext } from 'src/app/core/services/config/app.settings';
import { OperateursService } from 'src/app/data/intervenant/services/operateurs/operateurs.service';
import { MouvementsProduitsService } from 'src/app/data/declaration/services/mouvements/mouvements-produits.service';
import { ControlesService } from 'src/app/data/declaration/services/controles/controles.service';
import { EchantillonsService } from 'src/app/data/declaration/services/echantillons/echantillons.service';
import { forkJoin } from 'rxjs';
import { ManquementsService } from 'src/app/data/declaration/services/manquements/manquements.service';
import { CommissionAgenda } from 'src/app/data/commission/models/commission-agenda.model';
import { CommissionsService } from 'src/app/data/commission/services/commissions/commissions.service';
import * as moment from 'moment';
import { ModificationOperateur } from '../../../../../data/intervenant/models/modification-operateur.model';
import { OrganismesService } from '../../../../../data/intervenant/services/organismes/organismes.service';
import { Organisme } from '../../../../../data/intervenant/models/organisme.model';
import { RefCampagnesService } from '../../../../../data/declaration/services/ref-campagnes/ref-campagnes.service';
import { switchMap } from 'rxjs/operators';
import { DeclarationsService } from '../../../../../data/declaration/services/declarations/declarations.service';
import { TypeDr } from '../../../../../data/declaration/models/enums/type-dr.enum';
import { LoaderService } from '../../../../../core/services/loader/loader.service';
import { TypeModificationEnum } from '../../../../../data/intervenant/models/enums/type-modification.enum';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DashboardWidgetConfig, DashboardWidgetHeight, DashboardWidgetSize } from 'src/app/data/dashboard/dashboard-widget-config.model';
import { DashboardConfigService } from 'src/app/data/dashboard/dashboard-config.service';

@Component({
    selector: 'app-home-innov',
    templateUrl: './home-innov.component.html',
    styleUrls: ['./home-innov.component.scss'],
    standalone: false
})
export class HomeInnovComponent implements OnInit {
    static routes: Routes = [
        {
            path: '',
            data: {
                role: 'ORGANISME',
                domaines: ['VIN', 'ANPP'],
                menu: {
                    icon: 'tachometer-alt',
                    libelle: 'menu.home'
                }
            },
            component: HomeInnovComponent
        }
    ];

    isAnpp: boolean;
    userSearch: string;
    nbOperateurs: number;
    chartHabilitation: EChartsOption;
    habilitations: Record<string, number>;
    nbHabilites: number;
    nbEnCours: number;
    nbRefuses: number;
    nbRetires: number;
    nbSuspendus: number;
    nbDeclarationsNonLues: number;
    nbDrevManquantes: number;
    nbControlesPrevus: number;
    nbEchantillons: number[] = [];
    nbManquement: number;
    commissions: CommissionAgenda[];
    groupCommissions: any[] = [];

    totalDR = 0;
    nbDrUpdate = 0;
    nbDrevSaisie = 0;
    nbVinSansDrev = 0;
    annee: string;
    readonly TypeDr = TypeDr;

    modifications: ModificationOperateur[] = [];
    organismes: Organisme[] = [];

    customizeMode = false;
    widgets: DashboardWidgetConfig[] = [];

    get idIntervenant(): number {
        return SessionContext.get('idIntervenant');
    }

    get idOrganisme(): number {
        return SessionContext.get('idOrganisme');
    }

    get visibleWidgets(): DashboardWidgetConfig[] {
        return [...this.widgets].filter(widget => widget.visible).sort((a, b) => a.order - b.order);
    }

    get allWidgets(): DashboardWidgetConfig[] {
        return [...this.widgets].sort((a, b) => a.order - b.order);
    }

    getWidgetGridColumn(widget: DashboardWidgetConfig): string {
        switch (widget.size) {
            case 'small':
                return 'span 4';
            case 'medium':
                return 'span 6';
            case 'large':
                return 'span 8';
            case 'full':
                return 'span 12';
            default:
                return 'span 4';
        }
    }

    getWidgetGridRow(widget: DashboardWidgetConfig): string {
        switch (widget.height) {
            case 'small':
                return 'span 1';
            case 'medium':
                return 'span 2';
            case 'large':
                return 'span 3';
            default:
                return 'span 2';
        }
    }

    changeWidgetSize(widgetId: string, size: DashboardWidgetSize): void {
        const widget = this.widgets.find(w => w.id === widgetId);
        if (!widget || widget.locked) {
            return;
        }

        widget.size = size;
        this.saveDashboardConfig();
    }

    changeWidgetHeight(widgetId: string, height: DashboardWidgetHeight): void {
        const widget = this.widgets.find(w => w.id === widgetId);
        if (!widget || widget.locked) {
            return;
        }

        widget.height = height;
        this.saveDashboardConfig();
    }

    widgetSizes: { value: DashboardWidgetSize; label: string }[] = [
        { value: 'small', label: 'Petite' },
        { value: 'medium', label: 'Moyenne' },
        { value: 'large', label: 'Grande' },
        { value: 'full', label: 'Pleine largeur' }
    ];

    widgetHeights: { value: DashboardWidgetHeight; label: string }[] = [
        { value: 'small', label: 'Basse' },
        { value: 'medium', label: 'Moyenne' },
        { value: 'large', label: 'Haute' }
    ];

    constructor(
        private readonly habilitationService: HabilitationsService,
        private readonly operateurService: OperateursService,
        private readonly mouvementsProduitsService: MouvementsProduitsService,
        private readonly controleService: ControlesService,
        private readonly echantillonsService: EchantillonsService,
        private readonly manquementService: ManquementsService,
        private readonly commissionService: CommissionsService,
        private readonly loaderService: LoaderService,
        private readonly router: Router,
        private readonly organismesService: OrganismesService,
        private readonly refCampagnesService: RefCampagnesService,
        private readonly declarationsService: DeclarationsService,
        private readonly dashboardConfigService: DashboardConfigService
    ) {}

    ngOnInit() {
        this.widgets = this.dashboardConfigService.load(this.idIntervenant);

        this.refCampagnesService
            .getCampagneEnCours()
            .pipe(
                switchMap(campagne => {
                    this.annee = campagne.annee.toString();
                    return forkJoin({
                        nbDeclarationDouaneDp: this.declarationsService.nbDeclarationDouaneDp(this.idOrganisme, this.annee),
                        nbDeclarationDouaneDpModify: this.declarationsService.nbDeclarationDouaneDpModify(this.idOrganisme, this.annee),
                        nbDrevAOC: this.declarationsService.nbDrevAOC(this.idOrganisme, this.annee)
                    });
                })
            )
            .subscribe(result => {
                this.totalDR = result.nbDeclarationDouaneDp;
                this.nbDrUpdate = result.nbDeclarationDouaneDpModify;
                this.nbDrevSaisie = result.nbDrevAOC;
            });

        forkJoin({
            nbOperateurs: this.operateurService.getNombreOperateursAffilies(),
            habilitations: this.habilitationService.getOrganismeCountHabilitations(this.idOrganisme),
            nbDeclarationsNonLues: this.mouvementsProduitsService.countSuiviMouvement(),
            nbControle: this.controleService.countControlesPrevus(),
            nbEchantillons: this.echantillonsService.countEchantillonsEnCoursEtAPrelever(),
            nbDrevManquantes: this.operateurService.countOperateursDREVManquantes(this.idOrganisme),
            nbManquements: this.manquementService.countManquementEncours(),
            commissions: this.commissionService.getAgendaCommissions(this.idOrganisme, moment(), moment().add(7, 'days')),
            modifications: this.operateurService.getOperateursByOrganisme(this.idOrganisme).pipe(
                switchMap(operateurs => {
                    return this.operateurService.getModificationsOperateursGrouped(operateurs.map(o => o.id));
                })
            ),
            organismes: this.organismesService.getOrganismesAll()
        })
            .pipe(this.loaderService.operator())
            .subscribe(
                ({
                    nbOperateurs,
                    habilitations,
                    nbDeclarationsNonLues,
                    nbControle,
                    nbEchantillons,
                    nbDrevManquantes,
                    nbManquements,
                    commissions,
                    modifications,
                    organismes
                }) => {
                    this.nbOperateurs = nbOperateurs;
                    this.habilitations = habilitations;
                    this.nbDeclarationsNonLues = nbDeclarationsNonLues;
                    this.nbControlesPrevus = nbControle;
                    this.nbEchantillons = nbEchantillons;
                    this.nbDrevManquantes = nbDrevManquantes;
                    this.nbManquement = nbManquements;
                    this.commissions = commissions;
                    this.modifications = modifications
                        .sort((a, b) => new Date(b.dateModification).getTime() - new Date(a.dateModification).getTime())
                        .filter(m => m.idModificationType === TypeModificationEnum.MAJEURE)
                        .slice(0, 3);
                    this.organismes = organismes;

                    const reGroup = (list: CommissionAgenda[]) => {
                        const groups = [];
                        list.forEach(item => {
                            const dateComm = item.debut.format('dddd DD MMM');

                            let groupIndex = groups.findIndex(gi => gi.key === dateComm);
                            if (groupIndex === -1) {
                                groups.push({ key: dateComm, items: [] });
                                groupIndex = groups.length - 1;
                            }
                            const newItem = Object.assign({}, item);
                            groups[groupIndex].items.push(newItem);
                        });
                        return groups;
                    };

                    this.commissions.sort((a, b) => {
                        const nameA = a.debut;
                        const nameB = b.debut;
                        if (nameA < nameB) {
                            return -1;
                        }
                        if (nameA > nameB) {
                            return 1;
                        }
                        return 0;
                    });

                    this.groupCommissions = reGroup(this.commissions);
                    this.loadData();
                }
            );

        NavigationContext.set('idOperateur', undefined);
    }

    loadData(): void {
        this.nbHabilites = this.habilitations['HABILITE'] ?? 0;
        this.nbEnCours = this.habilitations['ENCOURS'] ?? 0;
        this.nbRefuses = this.habilitations['REFUSE'] ?? 0;
        this.nbRetires = this.habilitations['RETIRE'] ?? 0;
        this.nbSuspendus = this.habilitations['SUSPENDU'] ?? 0;

        this.chartHabilitation = {
            tooltip: {
                trigger: 'item'
            },
            legend: {
                top: '5%',
                left: 'auto'
            },
            series: [
                {
                    name: 'Habilitations',
                    type: 'pie',
                    radius: ['30%', '60%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2
                    },
                    label: {
                        show: false,
                        position: 'center'
                    },
                    emphasis: {
                        label: {
                            show: false,
                            fontSize: 40,
                            fontWeight: 'bold'
                        }
                    },
                    labelLine: {
                        show: false
                    },
                    data: [
                        { value: this.nbEnCours, name: 'En cours', itemStyle: { color: '#ac83d7' } },
                        { value: this.nbHabilites, name: 'Habilités', itemStyle: { color: '#a9d86e' } },
                        { value: this.nbRefuses, name: 'Refusés', itemStyle: { color: '#ff6c60' } },
                        { value: this.nbRetires, name: 'Retirés', itemStyle: { color: '#777777' } },
                        { value: this.nbSuspendus, name: 'Suspendus', itemStyle: { color: '#99CCFF' } }
                    ]
                }
            ]
        };
    }

    toggleCustomizeMode(): void {
        this.customizeMode = !this.customizeMode;
    }

    toggleWidget(widgetId: string): void {
        const widget = this.widgets.find(w => w.id === widgetId);
        if (!widget || widget.locked) {
            return;
        }

        widget.visible = !widget.visible;
        this.reorderWidgets();
        this.saveDashboardConfig();
    }

    resetDashboard(): void {
        this.widgets = this.dashboardConfigService.reset(this.idIntervenant);
    }

    drop(event: CdkDragDrop<DashboardWidgetConfig[]>): void {
        const visible = [...this.visibleWidgets];
        moveItemInArray(visible, event.previousIndex, event.currentIndex);

        visible.forEach((widget, index) => {
            const target = this.widgets.find(w => w.id === widget.id);
            if (target) {
                target.order = index + 1;
            }
        });

        const hidden = this.widgets.filter(w => !w.visible).sort((a, b) => a.order - b.order);

        hidden.forEach((widget, index) => {
            widget.order = visible.length + index + 1;
        });

        this.saveDashboardConfig();
    }

    private reorderWidgets(): void {
        const visible = this.widgets.filter(w => w.visible).sort((a, b) => a.order - b.order);

        const hidden = this.widgets.filter(w => !w.visible).sort((a, b) => a.order - b.order);

        visible.forEach((widget, index) => (widget.order = index + 1));
        hidden.forEach((widget, index) => (widget.order = visible.length + index + 1));
    }

    private saveDashboardConfig(): void {
        this.dashboardConfigService.save(this.widgets, this.idIntervenant);
    }

    trackByWidget(index: number, widget: DashboardWidgetConfig): string {
        return widget.id;
    }

    trackByGroupCommission(index: number, group: any): string {
        return group.key;
    }

    trackByCommission(index: number, item: CommissionAgenda): number {
        return item.id;
    }

    searchUser(): void {
        this.router.navigate(['/main/operateurs/or/recherche-affilie'], { queryParams: { search: this.userSearch } });
    }

    goToSuivi(): void {
        this.router.navigate(['/main/suivi-declarations/liste']);
    }

    goToManquantes(): void {
        this.router.navigate(['/main/drev-manquantes/liste']);
    }

    goToCommission(ca: CommissionAgenda): void {
        this.router.navigate(['/main/controles/commissions/' + ca.id + '/edit']);
    }

    gotToDrDrevSaisiesToDr(typeDR: TypeDr): void {
        this.router.navigate(['main/dr-drev-saisies/liste'], {
            state: {
                annee: this.annee,
                withDR: true,
                withDREV: null,
                typeDR
            }
        });
    }

    gotToDrDrevSaisiesToDrev(typeDR: TypeDr): void {
        this.router.navigate(['main/dr-drev-saisies/liste'], {
            state: {
                annee: this.annee,
                withDR: null,
                withDREV: true,
                typeDR
            }
        });
    }

    goToDrevSynthese() {
        this.router.navigate(['main/drev-synthese/liste']);
    }

    getOrganisme(idOrganisme: number): string {
        return this.hasOrganisme(idOrganisme) ? this.organismes.find(o => o.id === idOrganisme)?.raisonSociale ?? '' : '';
    }

    hasOrganisme(idOrganisme: number): boolean {
        return this.organismes.some(o => o.id === idOrganisme);
    }

    goToDetail() {
        this.router.navigate(['/main/historique-modification/liste']);
    }
}
